import './index.scss';

import React, {useContext, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import {expandProfileIdsForSelection, generateUniqueLinkName} from '../../tools';
import Modal from '../Modal';
import ImageGallery from '../ImageGallery';
import {fetchAllStorageImages} from '../../firebaseTools';
import {useSuggestions} from "../../containers/Tools/useSuggestions";
import {StateContext} from "../../App";

const EditLink = ({links, categories, profiles, link, onClose, onSave, onRemove}) => {
	const {addError} = useContext(StateContext);
	const selectableProfiles = useMemo(() => profiles, [profiles]);

	const [linkData, setLinkData] = useState({
		id: link.id,
		name: link.name,
		url: link.url,
		image: link.image,
		category: link.category,
		categoryId: link.categoryId,
		profileIds: Array.isArray(link.profileIds) ? link.profileIds : [],
	});

	const [imageMode, setImageMode] = useState('current');
	const [storageImages, setStorageImages] = useState([]);
	const [loadingImages, setLoadingImages] = useState(false);

	useEffect(() => {
		const loadStorageImages = async () => {
			setLoadingImages(true);
			const images = await fetchAllStorageImages(addError);
			setStorageImages(images);
			setLoadingImages(false);
		};
		loadStorageImages();
	}, [addError]);

	const {
		suggestions,
		showSuggestions,
		updateSuggestions,
		hideSuggestions
	} = useSuggestions(categories, cat => cat.category);

	const handleChange = (e) => {
		const {name, value} = e.target;
		setLinkData(prev => ({
			...prev,
			[name]: value
		}));

		if (name === 'category') {
			updateSuggestions(value);
		}
	};

	const handleSuggestionClick = (suggestion) => {
		setLinkData(prev => ({...prev, category: suggestion}));
		hideSuggestions();
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const expandedProfileIds = expandProfileIdsForSelection(linkData.profileIds, profiles);
		if (expandedProfileIds.length === 0) {
			return;
		}

		const updatedLinkData = {
			...linkData,
			profileIds: expandedProfileIds,
			name: generateUniqueLinkName(links, linkData.name, linkData.category, false, false, expandedProfileIds[0])
		};

		onSave({...link, ...updatedLinkData});
	};

	return (
		<Modal onClose={onClose}>
			<form onSubmit={handleSubmit} className="edit-link-form">
				<h2>Edit Link</h2>
				<div className="form-group">
					<label htmlFor="name">Name:</label>
					<input
						type="text"
						id="name"
						name="name"
						value={linkData.name}
						onChange={handleChange}
						required
					/>
				</div>
				<div className="form-group">
					<label htmlFor="category">Category:</label>
					<input
						type="text"
						id="category"
						name="category"
						value={linkData.category}
						onChange={handleChange}
						required
					/>
					{showSuggestions && (
						<ul className="category-suggestions">
							{suggestions.map((suggestion, index) => (
								<li key={`${suggestion}-${index}`}
								    onClick={() => handleSuggestionClick(suggestion)}>
									{suggestion}
								</li>
							))}
						</ul>
					)}
				</div>
				<div className="form-group">
					<label htmlFor="url">URL:</label>
					<input
						type="url"
						id="url"
						name="url"
						value={linkData.url}
						onChange={handleChange}
						required
					/>
				</div>
				<div className="form-group">
					<label>Image:</label>
					<div className="image-selection">
						<div className="image-selection__tabs">
							<button
								type="button"
								className={`image-selection__tab ${imageMode === 'current' ? 'image-selection__tab--active' : ''}`}
								onClick={() => {
									setImageMode('current');
									setLinkData(prev => ({...prev, image: link.image}));
								}}
							>
								Current
							</button>
							<button
								type="button"
								className={`image-selection__tab ${imageMode === 'upload' ? 'image-selection__tab--active' : ''}`}
								onClick={() => {
									setImageMode('upload');
									setLinkData(prev => ({...prev, image: null}));
								}}
							>
								Upload New
							</button>
							<button
								type="button"
								className={`image-selection__tab ${imageMode === 'select' ? 'image-selection__tab--active' : ''}`}
								onClick={() => {
									setImageMode('select');
									setLinkData(prev => ({...prev, image: null}));
								}}
								disabled={loadingImages}
							>
								{loadingImages ? 'Loading...' : 'Select Existing'}
							</button>
						</div>
						{imageMode === 'current' && (
							<div className="image-selection__preview">
								<img src={link.image} alt="Current" />
							</div>
						)}
						{imageMode === 'upload' && (
							<input
								type="file"
								name="image"
								placeholder="Image"
								onChange={(e) => setLinkData(prev => ({
									...prev,
									image: e.target.files[0]
								}))}
							/>
						)}
						{imageMode === 'select' && (
							<ImageGallery
								images={storageImages}
								selectedImage={typeof linkData.image === 'string' ? linkData.image : null}
								onSelect={(url) => setLinkData(prev => ({...prev, image: url}))}
							/>
						)}
					</div>
				</div>
				<div className="form-group">
					<label htmlFor="profiles">Profiles:</label>
					<select
						id="profiles"
						value={linkData.profileIds[0] || ''}
						onChange={(event) => {
							setLinkData((prev) => ({...prev, profileIds: [event.target.value]}));
						}}
					>
						{selectableProfiles.map((item) => (
							<option key={item.id} value={item.id}>
								{item.profileName}
							</option>
						))}
					</select>
				</div>
				<div className="form-actions">
					<button type="submit" className="btn-save">Save</button>
					<button type="button" className="btn-remove" onClick={() => onRemove(link)}>
						Remove
					</button>
					<button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
				</div>
			</form>
		</Modal>
	);
};

EditLink.propTypes = {
	links: PropTypes.array.isRequired,
	categories: PropTypes.array.isRequired,
	profiles: PropTypes.array.isRequired,
	link: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		url: PropTypes.string.isRequired,
		image: PropTypes.string.isRequired,
		category: PropTypes.string.isRequired,
		categoryId: PropTypes.string,
		profileIds: PropTypes.array
	}).isRequired,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired
};

export default EditLink;
